import React from 'react';

import { Alert, Button, CircularProgress, Stack, Typography } from '@mui/material';

import { useChatItinerarySnapshot } from '~/hooks/apis/trip-planner/useChatItinerarySnapshot';

import { snapshotDifference, snapshotLines } from './chatItinerarySnapshot';

interface Props {
  chatId: string;
  messageId: string;
  previousMessageId?: string;
  turnNumber: number;
}

export default function ChatTraceItinerary({ chatId, messageId, previousMessageId, turnNumber }: Props) {
  const current = useChatItinerarySnapshot(chatId, messageId);
  const previous = useChatItinerarySnapshot(chatId, previousMessageId);
  const lines = current.data ? snapshotLines(current.data) : [];
  const previousLines = previous.data ? snapshotLines(previous.data) : [];
  const added = snapshotDifference(lines, previousLines);
  const removed = snapshotDifference(previousLines, lines);

  return (
    <Stack spacing={1.5} sx={{ overflowWrap: 'anywhere' }}>
      <Typography variant="subtitle2">Itinerary after turn {turnNumber}</Typography>
      {current.isLoading && <CircularProgress size={20} aria-label="Loading itinerary" />}
      {current.isError && (
        <Alert severity="warning" action={<Button onClick={() => void current.refetch()}>Retry</Button>}>
          This turn’s itinerary snapshot is unavailable. It may not have been captured, or the chat may still be
          processing.
        </Alert>
      )}
      {current.data && (
        <>
          <Typography variant="subtitle2">Changes since previous turn</Typography>
          {!previousMessageId && <Typography variant="body2">First assistant turn.</Typography>}
          {previousMessageId && previous.isLoading && (
            <CircularProgress size={20} aria-label="Loading previous itinerary" />
          )}
          {previous.isError && (
            <Alert severity="warning" action={<Button onClick={() => void previous.refetch()}>Retry</Button>}>
              Previous snapshot unavailable. Changes cannot be compared.
            </Alert>
          )}
          {previous.data && (
            <Stack spacing={0.5}>
              {!added.length && !removed.length && (
                <Typography variant="body2">No changes to stops, nights, items or transport.</Typography>
              )}
              {removed.map((line, index) => (
                <Typography key={`removed-${index}`} variant="body2" color="error.main">
                  − {line}
                </Typography>
              ))}
              {added.map((line, index) => (
                <Typography key={`added-${index}`} variant="body2" color="success.main">
                  + {line}
                </Typography>
              ))}
            </Stack>
          )}
          <Typography variant="subtitle2">Full itinerary</Typography>
          {!lines.length && <Typography variant="body2">No stops or items in this snapshot.</Typography>}
          {lines.map((line, index) => (
            <Typography key={index} variant="body2">
              {line}
            </Typography>
          ))}
        </>
      )}
    </Stack>
  );
}
